import { observer } from 'mobx-react-lite';
import React, {
  forwardRef,
  HTMLAttributes,
  MutableRefObject,
  useEffect,
  useState,
} from 'react';
import { classes, st } from './NestedTableRow.st.css.js';
import { NestedTableKeyedItem } from '../../state';
import { FiltersMap } from '@wix/bex-core';
import { Box } from '@wix/design-system';
import { NestedTableRowPlaceholderStates } from './NestedTableRowPlaceholderStates';

export interface NestedTableRowProps<C extends string, T, F extends FiltersMap>
  extends HTMLAttributes<HTMLTableRowElement> {
  rowData: NestedTableKeyedItem<C, T, F>;
}

const _NestedTableRow = forwardRef(
  <C extends string, T, F extends FiltersMap>(
    props: NestedTableRowProps<C, T, F>,
    ref:
      | ((instance: HTMLTableRowElement | null) => void)
      | MutableRefObject<HTMLTableRowElement | null>
      | null,
  ) => {
    const { children, rowData, className, ...rest } = props;
    const { item } = rowData;

    const [itemState] = useState(() => {
      if (!item.originalKeyedItem) {
        return undefined;
      }

      return item.state.createNode({
        parent: item.originalKeyedItem,
      });
    });

    useEffect(() => itemState?.init(), []);

    if (!item.originalKeyedItem) {
      return (
        <tr ref={ref} className={st(classes.placeholder, className)} {...rest}>
          <td style={{ width: '100%' }}>
            <Box verticalAlign="middle" align="center" width="100%">
              <NestedTableRowPlaceholderStates state={item.state} />
            </Box>
          </td>
        </tr>
      );
    }

    const expandable =
      itemState != null && !itemState._isNotExpandableWhenEmpty;

    return (
      <tr
        ref={ref}
        className={st(
          classes.root,
          {
            expandable,
          },
          className,
          item.state.showHoverHighlight ? classes.parentHovered : undefined,
          item.state.depth === 0 ? classes.firstLevel : undefined,
        )}
        onMouseEnter={() => {
          itemState?._changeHover(true);
        }}
        onMouseLeave={() => {
          itemState?._changeHover(false);
        }}
        {...rest}
        onClick={(ev) => {
          rest?.onClick?.(ev);
          if (expandable) {
            itemState._toggleExpand();
          }
        }}
      >
        {children}
      </tr>
    );
  },
);

export const NestedTableRow = observer(_NestedTableRow);
